/**
 * Dubletten-Top-20-Report (Issue #384).
 *
 * Reine Datenlogik: Sortierung, Filter, Zeilen-Aufbereitung und CSV-Export.
 * Konsumiert die View `crm_kontakt_dubletten_admin` aus Migration #376; die
 * Kontakt-Namen werden aus `crm_kontakte` gejoint, weil die View nur IDs
 * liefert.
 */

import {
  serializeCsvCell,
  buildExportFilename,
  downloadBlob,
} from './forderungen-export';

export type MatchReason = 'email' | 'phone' | 'fuzzy';
export type MatchReasonFilter = MatchReason | 'alle';

export interface DublettenKandidat {
  kontakt_a_id: string;
  kontakt_b_id: string;
  match_reason: MatchReason;
  score: number;
  erkannt_am: string;
}

export interface CRMKontaktInfo {
  id: string;
  name: string;
  firma?: string | null;
}

export interface DublettenReportRow {
  kontakt_a_id: string;
  kontakt_b_id: string;
  kontakt_a_name: string;
  kontakt_b_name: string;
  kontakt_a_firma: string | null;
  kontakt_b_firma: string | null;
  match_reason: MatchReason;
  score: number;
  erkannt_am: string;
}

const MATCH_REASON_LABELS: Record<MatchReason, string> = {
  email: 'E-Mail',
  phone: 'Telefon',
  fuzzy: 'Unscharf',
};

export function matchReasonLabel(reason: MatchReason): string {
  return MATCH_REASON_LABELS[reason];
}

/** Stabile Sortierung nach Score absteigend, mit Limit. */
export function topNByScore<T extends { score: number }>(
  items: T[],
  n: number,
): T[] {
  return [...items].sort((a, b) => b.score - a.score).slice(0, n);
}

export function filterByMatchReason(
  items: DublettenKandidat[],
  reason: MatchReasonFilter,
): DublettenKandidat[] {
  if (reason === 'alle') return items;
  return items.filter((k) => k.match_reason === reason);
}

export function buildDublettenRows(
  kandidaten: DublettenKandidat[],
  kontakte: CRMKontaktInfo[],
): DublettenReportRow[] {
  const byId = new Map<string, CRMKontaktInfo>();
  for (const k of kontakte) byId.set(k.id, k);

  return kandidaten.map((k) => {
    const a = byId.get(k.kontakt_a_id);
    const b = byId.get(k.kontakt_b_id);
    return {
      kontakt_a_id: k.kontakt_a_id,
      kontakt_b_id: k.kontakt_b_id,
      kontakt_a_name: a?.name ?? '(unbekannt)',
      kontakt_b_name: b?.name ?? '(unbekannt)',
      kontakt_a_firma: a?.firma ?? null,
      kontakt_b_firma: b?.firma ?? null,
      match_reason: k.match_reason,
      score: k.score,
      erkannt_am: k.erkannt_am,
    };
  });
}

const BOM = '﻿';
const CRLF = '\r\n';
const DELIM = ';';

const CSV_COLUMNS: ReadonlyArray<{ label: string; value: (r: DublettenReportRow) => string }> = [
  { label: 'Kontakt A', value: (r) => r.kontakt_a_name },
  { label: 'Firma A', value: (r) => r.kontakt_a_firma ?? '' },
  { label: 'Kontakt B', value: (r) => r.kontakt_b_name },
  { label: 'Firma B', value: (r) => r.kontakt_b_firma ?? '' },
  { label: 'Match-Grund', value: (r) => matchReasonLabel(r.match_reason) },
  { label: 'Score', value: (r) => String(r.score) },
  { label: 'Erkannt am', value: (r) => formatErkanntAm(r.erkannt_am) },
];

function formatErkanntAm(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
}

export function toDublettenCsv(rows: DublettenReportRow[]): string {
  const header = CSV_COLUMNS.map((c) => serializeCsvCell(c.label)).join(DELIM);
  if (rows.length === 0) return `${BOM}${header}${CRLF}`;
  const body = rows
    .map((r) => CSV_COLUMNS.map((c) => serializeCsvCell(c.value(r))).join(DELIM))
    .join(CRLF);
  return `${BOM}${header}${CRLF}${body}${CRLF}`;
}

export function downloadDublettenCsv(rows: DublettenReportRow[]): void {
  const csv = toDublettenCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, buildExportFilename('dubletten-top20', 'csv'));
}
